import { Box, Button, Group, JsonInput, SimpleGrid, Stack, Tabs, TextInput, Title } from "@mantine/core";
import { modals } from "@mantine/modals";
import { IconFileCode2, IconMap2, IconSparkles } from "@tabler/icons-react";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { DevtoolCard } from "routes/_auth/_dev/devtools";
import { httpPostGraphql } from "shared/api/httpClient";
import { Content } from "shared/components/global/Content";
import { MapBase } from "shared/components/map/MapBase";
import { MapBoundaryOverlay } from "shared/components/map/MapBoundaryOverlay";
import { MapGeoFilterOverlay } from "shared/components/map/MapGeoFilterOverlay";
import { MapPolygonDrawOverlay } from "shared/components/map/MapPolygonDrawOverlay";
import { useGraphqlMutation } from "shared/hooks/useGraphql";
import { useIsMobile } from "shared/hooks/useIsMobile";
import { catching, to } from "shared/utils/fns";
import { maybe } from "shared/utils/maybe";
import { notify } from "shared/utils/notify";
import { qk } from "shared/utils/qk";
import { geozod } from "shared/utils/zod";
import { graphql } from "src/gql";

const component = () => {
    const { data } = useSuspenseQuery(queries.geofilters);
    const isMobile = useIsMobile();

    const remove = useGraphqlMutation(
        graphql(`
            mutation DevtoolsReionArchiveGeofilter($id: UUID!) {
                archiveGeoFilter(id: $id) {
                    id
                }
            }
        `),
    );

    async function handleArchive(geofilter: { id: string; name: string; boundary: string }) {
        await notify.confirm("Are you sure you want to delete this geofilter?", {
            title: "Confirm",
        });
        await remove
            .mutateAsync(geofilter)
            .then(() => qk.invalidate("map", "customer"))
            .then(() => notify.show.success("Archived geofilter"))
            .catch(notify.catch);
        modals.closeAll();
    }

    function handleNew() {
        function Panel() {
            const [polygon, setPolygon] = useState("");
            const [name, setName] = useState("");

            const save = useGraphqlMutation(
                graphql(`
                    mutation DevtoolsRegionCreateGeofilter($name: String!, $boundary: GeoPolygon!) {
                        createGeoFilter(parameters: { boundary: $boundary, name: $name }) {
                            id
                        }
                    }
                `),
            );

            return (
                <Stack w="100%">
                    <Tabs defaultValue="Map" variant="outline">
                        <Tabs.List>
                            <Tabs.Tab value="Map" leftSection={<IconMap2 size={16} />}>
                                Map
                            </Tabs.Tab>
                            <Tabs.Tab value="GeoJSON" leftSection={<IconFileCode2 size={16} />}>
                                GeoJSON
                            </Tabs.Tab>
                        </Tabs.List>
                        <Tabs.Panel value="Map">
                            <Box pos="relative" h={isMobile ? "50dvh" : "65dvh"} w={"100%"} style={{ overflow: "clip" }}>
                                <MapBase id="devtools-geofilter-create">
                                    <MapBoundaryOverlay />
                                    <MapPolygonDrawOverlay
                                        position="top-left"
                                        features={maybe(geozod.feature(geozod.polygon()).safeParse(polygon).data)?.take((it) => [it]) ?? []}
                                        onChange={(features) =>
                                            setPolygon(JSON.stringify(geozod.polygon().parse(features.at(0)?.geometry)))
                                        }
                                    />
                                </MapBase>
                            </Box>
                        </Tabs.Panel>
                        <Tabs.Panel value="GeoJSON">
                            <JsonInput
                                minRows={8}
                                autosize
                                label={"Polygon"}
                                placeholder='{ "type": "polygon", ...'
                                value={polygon}
                                onChange={(e) => setPolygon(e)}
                                error={maybe(catching(() => JSON.parse(polygon)))?.take((obj) =>
                                    geozod.polygon().safeParse(obj).error?.errors.map(to("message")).join("\n"),
                                )}
                            />

                            <Group justify="flex-end">
                                <Button
                                    variant="transparent"
                                    td="underline"
                                    leftSection={<IconSparkles size={16} />}
                                    onClick={() => {
                                        catching(() => setPolygon(JSON.stringify(JSON.parse(polygon), null, 2)));
                                    }}
                                >
                                    Format
                                </Button>
                            </Group>
                        </Tabs.Panel>
                    </Tabs>
                    <TextInput label="Geofilter Name" onChange={(e) => setName(e.target.value)} />
                    <Group justify="flex-end">
                        <Button variant="default" onClick={() => modals.closeAll()}>
                            Cancel
                        </Button>
                        <Button
                            onClick={() =>
                                void save
                                    .mutateAsync({ name, boundary: JSON.stringify(JSON.parse(polygon)) })
                                    .then(() => modals.closeAll())
                                    .then(() => qk.invalidate("map"))
                                    .catch(notify.catch)
                            }
                            loading={save.isPending}
                            disabled={name.trim().length == 0 || polygon == ""}
                        >
                            Create
                        </Button>
                    </Group>
                </Stack>
            );
        }

        modals.open({
            title: "New Geofilter",
            size: "xl",
            children: <Panel />,
        });
    }

    return (
        <Content paper>
            <Content.Heading backable>
                <Title order={2}>Edit Customer GeoFilters (regions)</Title>
            </Content.Heading>

            <Content.Action>
                <Button onClick={handleNew}>New</Button>
            </Content.Action>

            <SimpleGrid cols={isMobile ? 1 : 2} h="full">
                {data.map((geofilter) => (
                    <DevtoolCard key={geofilter.id} rightSection={<Button onClick={() => void handleArchive(geofilter)}>Archive</Button>}>
                        {geofilter.name}
                    </DevtoolCard>
                ))}
            </SimpleGrid>

            <Box h="50dvh" w="100%" pos="relative">
                <MapBase id="devtools-geofilters-aggregate">
                    <MapBoundaryOverlay />
                    <MapGeoFilterOverlay geofilterIds={[]} onChange={() => {}} />
                </MapBase>
            </Box>
        </Content>
    );
};

const queries = {
    geofilters: queryOptions({
        queryKey: qk("map", "customer", "geofilters"),
        select: to("geoFilters"),
        queryFn: () =>
            httpPostGraphql(
                graphql(`
                    query DevtoolsRegions {
                        geoFilters(limit: 1000, offset: 0) {
                            id
                            name
                            boundary
                        }
                    }
                `),
                {},
            ),
    }),
};

/** @public */
export const Route = createFileRoute("/_auth/_dev/devtools/regions")({
    component,
});
